import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme } from '../hooks/useTheme';
import { Spacing, Typography } from '../theme/tokens';
import { formatBytes, formatSpeed, formatEta } from '../../../shared/utils/formatters';

interface StatusLineProps {
  speed: number; // download bytes/sec
  uploadSpeed?: number; // upload bytes/sec
  bytesTransferred: number;
  totalBytes: number;
  eta: number; // seconds
  peersCount?: number;
  seedersCount?: number;
}

export const StatusLine: React.FC<StatusLineProps> = ({
  speed,
  uploadSpeed = 0,
  bytesTransferred,
  totalBytes,
  eta,
  peersCount = 0,
  seedersCount = 0,
}) => {
  const { colors } = useTheme();

  return (
    <View style={styles.wrapper}>
      {/* Upper line: Bytes & Speeds */}
      <View style={styles.row}>
        <Text style={[styles.text, { color: colors.subtext }]}>
          {formatBytes(bytesTransferred)} / {formatBytes(totalBytes)}
        </Text>
        <View style={styles.spacer} />
        <Text style={[styles.text, styles.speedText, { color: colors.brand }]}>
          ↓ {formatSpeed(speed)}
        </Text>
        {uploadSpeed > 0 && (
          <Text style={[styles.text, styles.speedText, { color: colors.success ?? colors.brand, marginLeft: Spacing.sm }]}>
            ↑ {formatSpeed(uploadSpeed)}
          </Text>
        )}
      </View>

      {/* Lower line: Swarm Peers & ETA */}
      <View style={[styles.row, styles.metaRow]}>
        <Text style={[styles.metaText, { color: colors.subtext }]}>
          {peersCount > 0
            ? `Peers: ${peersCount}${seedersCount > 0 ? ` (${seedersCount} seeders)` : ''}`
            : 'Swarm: Connecting to trackers...'}
        </Text>
        <View style={styles.spacer} />
        <Text style={[styles.metaText, { color: colors.subtext }]}>
          {formatEta(eta)}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
    marginTop: Spacing.xs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  metaRow: {
    marginTop: Spacing.xs,
  },
  text: {
    fontSize: Typography.sizeXs,
    fontWeight: Typography.weightMedium,
  },
  speedText: {
    fontFamily: 'monospace',
    fontWeight: Typography.weightBold,
  },
  metaText: {
    fontSize: Typography.sizeXs - 1,
    opacity: 0.8,
  },
  spacer: {
    flex: 1,
  },
});
